'use client';

import React, { useEffect, useRef } from 'react';
import { useRenderContext } from '../../context/RenderContext';
import { filterSuppliers } from '../../lib/filterSuppliers';
import { STATUS_META, statusLabel } from '../../lib/status';

import type { Supplier } from '../../types';

interface SupplierMapProps {
  onSelectSupplier?: (supplier: Supplier) => void;
  suppliersList?: Supplier[];
  className?: string;
}

export function SupplierMap({ onSelectSupplier, suppliersList, className }: SupplierMapProps = {}): React.ReactElement {
  const { suppliers, selectedPolo, selectedSupplier, selectSupplier, isLoading } = useRenderContext();
  const mapRef = useRef<HTMLDivElement | null>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  const filteredSuppliers = suppliersList ?? filterSuppliers(suppliers, { polo: selectedPolo });

  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;

    async function initMap(): Promise<void> {
      try {
        const L = await import('leaflet');
        if (!mapRef.current) return;

        const map = L.map(mapRef.current, {
          center: [-23.5505, -46.6333],
          zoom: 7,
          zoomControl: false,
          keyboard: true
        });

        const isLight = document.documentElement.getAttribute('data-theme') === 'light' || document.documentElement.classList.contains('light-theme');
        const initialTileUrl = isLight
          ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png'
          : 'https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png';

        const tileLayer = L.tileLayer(initialTileUrl, {
          attribution: '&copy; CartoDB &copy; OpenStreetMap',
          subdomains: 'abcd',
          maxZoom: 19
        }).addTo(map);
        tileLayerRef.current = tileLayer;

        L.control.zoom({ position: 'bottomright' }).addTo(map);

        markersRef.current = L.layerGroup().addTo(map);
        leafletMapRef.current = map;
        setTimeout(() => {
          map.invalidateSize();
        }, 100);
      } catch (e: unknown) {
        void e;
      }
    }

    void initMap();

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        tileLayerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!tileLayerRef.current) return;
      const isLightNow = document.documentElement.getAttribute('data-theme') === 'light' || document.documentElement.classList.contains('light-theme');
      const newUrl = isLightNow
        ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png'
        : 'https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png';
      tileLayerRef.current.setUrl(newUrl);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!leafletMapRef.current || !markersRef.current) return;

    async function updateMarkers(): Promise<void> {
      const L = await import('leaflet');
      if (!markersRef.current || !leafletMapRef.current) return;

      markersRef.current.clearLayers();
      leafletMapRef.current.invalidateSize();

      filteredSuppliers.forEach(supplier => {

        const lat = supplier.latitude || supplier.lat;
        const lng = supplier.longitude || supplier.lng;

        if (!lat || !lng) return;

        const isSelected = selectedSupplier?.id === supplier.id;
        const color = STATUS_META[supplier.status].color;

        const customIcon = L.divIcon({
          className: 'custom-leaflet-pin',
          html: `<div style="
            width: ${isSelected ? '22px' : '14px'};
            height: ${isSelected ? '22px' : '14px'};
            background: ${color};
            border: 2px solid #ffffff;
            border-radius: 50%;
            box-shadow: 0 0 12px ${color};
            transition: all 0.3s ease;
          "></div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });

        const marker = L.marker([lat, lng], { icon: customIcon });

        const popupContent = `
          <div style="font-family: sans-serif; padding: 4px;">
            <p style="font-size: 10px; font-weight: 700; color: #a39b94; text-transform: uppercase;">${supplier.category}</p>
            <h4 style="font-size: 13px; font-weight: 800; color: #faf7f5; margin: 2px 0;">${supplier.name}</h4>
            <p style="font-size: 11px; color: #a39b94; margin-bottom: 6px;">📍 ${supplier.city} - ${supplier.state}</p>
            <span role="status" class="badge-status ${supplier.status} inline-flex items-center">
              <span class="w-1.5 h-1.5 rounded-full bg-current animate-pulse inline-block mr-1"></span>
              ${statusLabel(supplier.status)}
            </span>
          </div>
        `;

        marker.bindPopup(popupContent);
        marker.on('click', () => {
          selectSupplier(supplier);
          if (onSelectSupplier) {
            onSelectSupplier(supplier);
          }
        });
        markersRef.current?.addLayer(marker);
      });
    }

    void updateMarkers();
  }, [filteredSuppliers, selectedSupplier, selectSupplier, onSelectSupplier]);

  // Pan map when selectedSupplier changes
  useEffect(() => {
    if (!leafletMapRef.current || !selectedSupplier) return;
    const lat = selectedSupplier.latitude || selectedSupplier.lat;
    const lng = selectedSupplier.longitude || selectedSupplier.lng;
    if (lat && lng) {
      leafletMapRef.current.flyTo([lat, lng], 13, { duration: 1.2 });
    }
  }, [selectedSupplier]);

  return (
    <div id="supplier-map-container" className={`relative rounded-2xl overflow-hidden glass-panel border border-white/10 h-[480px] shadow-2xl isolate z-10 ${className || ''}`}>
      <div className="absolute top-4 left-4 z-10 bg-[#0c0a0b]/90 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/10 text-xs flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 shadow-lg">
        <div>
          <span className="text-[9px] font-bold text-[#e11d48] uppercase tracking-wider block">✦ Radar de Polos B2B</span>
          <span className="font-bold text-[#faf7f5] text-xs">Geomapeamento Comercial</span>
        </div>
        <span className="px-2.5 py-1 rounded-lg bg-[#e11d48]/20 text-[#e11d48] font-mono font-bold text-xs border border-[#e11d48]/30 shrink-0">
          POLO {selectedPolo} ({filteredSuppliers.length} Unidades)
        </span>
      </div>

      {!isLoading && filteredSuppliers.length === 0 && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <p className="px-4 py-2 rounded-xl bg-[#0c0a0b]/90 border border-white/10 text-xs text-[#a39b94]">
            Nenhum fornecedor no polo {selectedPolo}. Altere o filtro no topo.
          </p>
        </div>
      )}

      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}
